<template>
  <div class="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 flex items-center justify-center">
    <div class="bg-white/10 backdrop-blur-md rounded-2xl p-8 shadow-2xl border border-white/20 max-w-md w-full mx-4">
      <div class="text-center">
        <!-- Logo -->
        <div class="mb-6">
          <div class="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <h1 class="text-2xl font-bold text-white mb-2">Autenticación Exitosa</h1>
          <p class="text-blue-100">Panel de Administración</p>
        </div>

        <!-- Estado del callback -->
        <div class="space-y-4">
          <div v-if="isProcessing" class="flex items-center justify-center space-x-3">
            <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
            <span class="text-white font-medium">{{ statusMessage }}</span>
          </div>
          <div class="mb-6">
            <div class="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" 
                 :class="hasError ? 'bg-red-600' : 'bg-blue-600'">
              <svg v-if="isProcessing" class="w-8 h-8 text-white animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <svg v-else-if="hasError" class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <svg v-else class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 class="text-2xl font-bold text-gray-900 mb-2">Panel de Administración</h2>
            <p class="text-gray-600" :class="hasError ? 'text-red-600' : 'text-gray-600'">{{ statusMessage }}</p>
          </div>
          <div class="text-sm text-blue-100">
            <span v-if="isProcessing">Procesando autenticación...</span>
            <span v-else-if="hasError">Redirigiendo al login...</span>
            <span v-else>Redirigiendo al panel...</span>
          </div>
        </div>

        <!-- Información adicional -->
        <div class="mt-8 text-xs text-blue-200 space-y-1">
          <p v-if="isProcessing">Verificando permisos de administrador</p>
          <p v-else-if="hasError">Por favor, intenta nuevamente</p>
          <p v-else>Acceso autorizado</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const router = useRouter()
const authStore = useAuthStore()

const isProcessing = ref(true)
const hasError = ref(false)
const statusMessage = ref('Procesando autenticación...')

const updateStatus = (processing: boolean, error: boolean, message: string) => {
  isProcessing.value = processing
  hasError.value = error
  statusMessage.value = message
}

onMounted(async () => {
  try {
    console.log('🔄 AuthCallbackView: Procesando callback...')
    
    updateStatus(true, false, 'Verificando respuesta de autenticación...')
    await new Promise(resolve => setTimeout(resolve, 500))
    
    // Verificar parámetros de URL
    const urlParams = new URLSearchParams(window.location.search)
    const hasErrorParam = urlParams.has('error')
    
    if (hasErrorParam) {
      const error = urlParams.get('error')
      const errorDescription = urlParams.get('error_description')
      console.error('❌ AuthCallbackView: Error en callback de Keycloak:', error, errorDescription)
      
      updateStatus(false, true, `Error de autenticación: ${errorDescription || error}`)
      
      setTimeout(() => {
        router.push('/login')
      }, 3000)
      return
    }
    
    updateStatus(true, false, 'Validando credenciales...')
    await new Promise(resolve => setTimeout(resolve, 300))
    
    // Procesar el callback de autenticación
    const success = await authStore.processCallback()
    
    if (success) {
      console.log('✅ AuthCallbackView: Callback procesado exitosamente')
      
      updateStatus(true, false, 'Verificando permisos de administrador...')
      await new Promise(resolve => setTimeout(resolve, 300))
      
      // Verificar que el usuario tenga rol de administrador
      if (authStore.isAdmin) {
        console.log('✅ AuthCallbackView: Usuario con permisos de admin, redirigiendo al dashboard')
        updateStatus(false, false, 'Acceso autorizado. Redirigiendo al panel...')
        
        // Pequeña pausa para mostrar el mensaje
        setTimeout(() => {
          router.push('/dashboard')
        }, 1000)
      } else {
        console.warn('⚠️ AuthCallbackView: Usuario sin permisos de administrador')
        updateStatus(false, true, 'Acceso denegado: Se requieren permisos de administrador')
        
        setTimeout(() => {
          router.push('/login')
        }, 3000)
      }
    } else {
      console.error('❌ AuthCallbackView: Error procesando callback')
      updateStatus(false, true, 'Error validando la autenticación')
      
      setTimeout(() => {
        router.push('/login')
      }, 2000)
    }
  } catch (error) {
    console.error('❌ AuthCallbackView: Error procesando callback', error)
    updateStatus(false, true, 'Error de conexión con el servidor')
    
    setTimeout(() => {
      router.push('/login')
    }, 2000)
  }
})
</script>
